/**
 * Input System - Handles keyboard and touch input for player control
 * Supports Arrow keys, WASD, and touch/drag controls
 */

import type { Position } from '../types';

/**
 * Input vector with normalized movement direction
 */
export interface InputVector {
  x: number;
  y: number;
}

/**
 * Control scheme types
 */
export type ControlScheme = 'keyboard' | 'touch' | 'none';

/**
 * Touch tracking data
 */
interface TouchData {
  id: number;
  startPos: Position;
  currentPos: Position;
}

/**
 * Input System Class
 */
export class InputSystem {
  private activeKeys: Set<string> = new Set();
  private touchData: TouchData | null = null;
  private currentVector: InputVector = { x: 0, y: 0 };
  private controlScheme: ControlScheme = 'none';
  private isEnabled: boolean = false;

  // Keyboard event handlers (bound to instance)
  private handleKeyDown = (e: KeyboardEvent) => {
    const key = e.key.toLowerCase();
    if (this.isMovementKey(key)) {
      e.preventDefault();
      this.activeKeys.add(key);
      this.updateControlScheme('keyboard');
      this.updateInputVector();
    }
  };

  private handleKeyUp = (e: KeyboardEvent) => {
    const key = e.key.toLowerCase();
    this.activeKeys.delete(key);
    this.updateInputVector();
  };

  // Touch event handlers (bound to instance)
  private handleTouchStart = (e: TouchEvent) => {
    if (e.touches.length > 0) {
      e.preventDefault();
      const touch = e.touches[0];
      this.touchData = {
        id: touch.identifier,
        startPos: { x: touch.clientX, y: touch.clientY },
        currentPos: { x: touch.clientX, y: touch.clientY }
      };
      this.updateControlScheme('touch');
    }
  };

  private handleTouchMove = (e: TouchEvent) => {
    if (!this.touchData) return;

    for (let i = 0; i < e.touches.length; i++) {
      const touch = e.touches[i];
      if (touch.identifier === this.touchData.id) {
        e.preventDefault();
        this.touchData.currentPos = { x: touch.clientX, y: touch.clientY };
        this.updateInputVectorFromTouch();
        break;
      }
    }
  };

  private handleTouchEnd = (e: TouchEvent) => {
    if (!this.touchData) return;

    let touchEnded = true;
    for (let i = 0; i < e.touches.length; i++) {
      if (e.touches[i].identifier === this.touchData.id) {
        touchEnded = false;
        break;
      }
    }

    if (touchEnded) {
      e.preventDefault();
      this.touchData = null;
      this.currentVector = { x: 0, y: 0 };
    }
  };

  /**
   * Initialize the input system and start listening for events
   */
  start(): void {
    if (this.isEnabled) return;

    this.isEnabled = true;

    // Add keyboard listeners
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);

    // Add touch listeners
    window.addEventListener('touchstart', this.handleTouchStart, { passive: false });
    window.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    window.addEventListener('touchend', this.handleTouchEnd, { passive: false });
    window.addEventListener('touchcancel', this.handleTouchEnd, { passive: false });
  }

  /**
   * Stop the input system and remove all event listeners
   */
  stop(): void {
    if (!this.isEnabled) return;

    this.isEnabled = false;

    // Remove keyboard listeners
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);

    // Remove touch listeners
    window.removeEventListener('touchstart', this.handleTouchStart);
    window.removeEventListener('touchmove', this.handleTouchMove);
    window.removeEventListener('touchend', this.handleTouchEnd);
    window.removeEventListener('touchcancel', this.handleTouchEnd);

    // Reset state
    this.activeKeys.clear();
    this.touchData = null;
    this.currentVector = { x: 0, y: 0 };
    this.controlScheme = 'none';
  }

  /**
   * Get the current input vector (normalized for diagonal movement)
   */
  getInputVector(): InputVector {
    return { ...this.currentVector };
  }

  /**
   * Get the current control scheme
   */
  getControlScheme(): ControlScheme {
    return this.controlScheme;
  }

  /**
   * Check if any input is currently active
   */
  hasInput(): boolean {
    return this.currentVector.x !== 0 || this.currentVector.y !== 0;
  }

  /**
   * Check if a specific key is pressed
   */
  isKeyPressed(key: string): boolean {
    return this.activeKeys.has(key.toLowerCase());
  }

  /**
   * Check if the system is enabled
   */
  isActive(): boolean {
    return this.isEnabled;
  }

  /**
   * Update the input vector based on active keys
   */
  private updateInputVector(): void {
    let x = 0;
    let y = 0;

    // Horizontal input
    if (this.activeKeys.has('arrowleft') || this.activeKeys.has('a')) {
      x -= 1;
    }
    if (this.activeKeys.has('arrowright') || this.activeKeys.has('d')) {
      x += 1;
    }

    // Vertical input
    if (this.activeKeys.has('arrowup') || this.activeKeys.has('w')) {
      y -= 1;
    }
    if (this.activeKeys.has('arrowdown') || this.activeKeys.has('s')) {
      y += 1;
    }

    // Normalize diagonal movement (√2 compensation)
    if (x !== 0 && y !== 0) {
      const length = Math.sqrt(x * x + y * y);
      x /= length;
      y /= length;
    }

    this.currentVector = { x, y };
  }

  /**
   * Update input vector from touch data
   */
  private updateInputVectorFromTouch(): void {
    if (!this.touchData) return;

    const dx = this.touchData.currentPos.x - this.touchData.startPos.x;
    const dy = this.touchData.currentPos.y - this.touchData.startPos.y;

    // Dead zone to prevent jitter
    const deadZone = 10;
    if (Math.abs(dx) < deadZone && Math.abs(dy) < deadZone) {
      this.currentVector = { x: 0, y: 0 };
      return;
    }

    // Normalize the vector
    const length = Math.sqrt(dx * dx + dy * dy);
    this.currentVector = {
      x: dx / length,
      y: dy / length
    };
  }

  /**
   * Check if a key is a movement key
   */
  private isMovementKey(key: string): boolean {
    const movementKeys = [
      'arrowup', 'arrowdown', 'arrowleft', 'arrowright',
      'w', 'a', 's', 'd'
    ];
    return movementKeys.includes(key);
  }

  /**
   * Update the detected control scheme
   */
  private updateControlScheme(scheme: ControlScheme): void {
    this.controlScheme = scheme;
  }
}

// Singleton instance for global use
export const inputSystem = new InputSystem();
